import {
  Component, DestroyRef, EnvironmentInjector, Input, OnChanges, OnInit, SimpleChanges, inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { DatePicker } from 'primeng/datepicker';
import { InputNumber } from 'primeng/inputnumber';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, forkJoin, of } from 'rxjs';
import {
  ProjectTask,
  ProjectTaskEffort,
  CreateProjectTaskDto,
  ProjectTaskService,
  PROJECT_TASK_PROGRESS_OPTIONS,
  PROJECT_TASK_PERCENT_OPTIONS,
} from '../../services/project-task.service';
import { EmployeeService } from '../../services/employee.service';
import { AuthService } from '../../services/auth.service';
import { Employee } from '../../models/employee.model';

/** One sprint's worth of tasks, as the table groups them. */
interface SprintGroup {
  sprint: string;
  tasks: ProjectTask[];
  doneCount: number;
}

/**
 * A project's sprint backlog.
 *
 * Sits alongside the weekly and milestone plans rather than replacing them: those answer
 * "what is this week about" and "what has the client been promised", this is the work
 * underneath.
 */
@Component({
  selector: 'app-project-tasks',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    InputText,
    Select,
    MultiSelect,
    DatePicker,
    InputNumber,
    Tooltip,
    Tag,
    ConfirmDialog,
  ],
  // ConfirmationService only. It must be component-scoped so this tab's own
  // <p-confirmDialog> answers its confirm() calls.
  //
  // MessageService is deliberately NOT listed, but that alone is not enough: the parent
  // Project Details component provides it component-scoped and renders no <p-toast>, so a
  // plain inject() here resolves to that dead instance rather than the root one the toast
  // in app.html listens to. See the injector note on the messageService field below.
  providers: [ConfirmationService],
  templateUrl: './project-tasks.component.html',
  styleUrl: './project-tasks.component.scss',
})
export class ProjectTasksComponent implements OnInit, OnChanges {
  @Input() projectId = '';

  /**
   * Hides delete and suppresses the page heading, for the Work Log copy of this tab.
   * <para>
   * Developers may add, edit and log effort on their allocated projects but not delete —
   * deleting stays a manager action in the Projects module. Enforced here only for the UI;
   * the Work Log page reaches the same endpoints, so this is a narrowing of what is offered,
   * not a security boundary.
   * </para>
   */
  @Input() readonlyDelete = false;

  /** Suppresses the "Tasks" heading when the page already names the section. */
  @Input() embedded = false;

  private destroyRef = inject(DestroyRef);
  private taskService = inject(ProjectTaskService);
  private employeeService = inject(EmployeeService);
  // Resolved from the ROOT injector, not the nearest one. Project Details provides its own
  // MessageService without a <p-toast>, so a plain inject() would hand us that instance and
  // every toast from this tab — success and error alike — would disappear silently, making
  // a working save look like a dead button.
  private messageService = inject(EnvironmentInjector).get(MessageService);
  private confirmationService = inject(ConfirmationService);
  private authService = inject(AuthService);

  tasks: ProjectTask[] = [];
  employees: Employee[] = [];
  loading = false;
  canManage = false;

  // Filters
  searchText = '';
  selectedSprints: string[] = [];
  selectedOwners: string[] = [];
  selectedProgress: string[] = [];

  readonly progressOptions = PROJECT_TASK_PROGRESS_OPTIONS;

  /**
   * Percentage choices for the dialog. A Done task may read 100%, nothing else may — the
   * server enforces exactly that, so the option only appears when it can actually be saved.
   *
   * Both arrays are built once. A getter returning a fresh array each call would hand
   * change detection a new reference every cycle, which is what makes a PrimeNG overlay
   * thrash — the same fault that froze the date picker on the tracker.
   */
  private readonly percentOptionsBase = PROJECT_TASK_PERCENT_OPTIONS;
  private readonly percentOptionsWithFull = [
    ...PROJECT_TASK_PERCENT_OPTIONS,
    { label: '100%', value: 100 },
  ];

  get percentOptions(): { label: string; value: number }[] {
    return this.form.progress === 'Done'
      ? this.percentOptionsWithFull
      : this.percentOptionsBase;
  }

  /**
   * Rounds a stored percentage to the nearest option the dropdown offers, so an existing
   * value always selects something rather than leaving the control blank.
   */
  private snapPercent(value: number, progress: string): number {
    if (progress === 'Done') {
      return 100;
    }
    const clamped = Math.min(Math.max(value, 0), 99);
    return Math.min(Math.round(clamped / 25) * 25, 75);
  }

  // Dialog
  displayDialog = false;
  isEditMode = false;
  editingId: string | null = null;
  /** The task open in the edit dialog, so its effort history can be reached from there. */
  editingTask: ProjectTask | null = null;
  form: CreateProjectTaskDto = this.emptyForm();
  startDate: Date | null = null;
  targetDate: Date | null = null;
  completedDate: Date | null = null;
  saveInFlight = false;

  /**
   * Hours to add on this save. Always starts empty, including when editing a task that
   * already has effort — it is an amount to ADD, not the total. Prefilling it with the
   * running total would make a plain re-save silently double the hours.
   */
  effortHours: number | null = null;
  effortNotes: string | null = null;

  /** The task's existing total, pre-formatted, so "adds to" has a figure. */
  existingEffortDisplay = '0h 0min';

  /**
   * True when the entered value's fractional part exceeds .59. In the hours.minutes
   * convention that is not a time — 2.75 almost certainly means 2h 45m typed as a decimal,
   * so the form says so rather than storing something that reads as 2h 75m.
   */
  get effortMinutesInvalid(): boolean {
    if (!this.effortHours || this.effortHours <= 0) {
      return false;
    }
    const whole = Math.floor(this.effortHours);
    return Math.round((this.effortHours - whole) * 100) > 59;
  }

  // Quick effort log, opened from the row rather than the task form.
  displayEffortDialog = false;
  effortTask: ProjectTask | null = null;
  effortDate: Date | null = null;
  effortInFlight = false;
  fieldTouched: Record<string, boolean> = {};

  /**
   * The number a new task will get if Task No is left blank. Computed here only to show it
   * in the dialog — the server works it out again on save, so two people adding at once
   * each still get a number rather than both trusting a stale preview.
   */
  nextTaskNumber = '';

  ngOnInit(): void {
    this.authService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        // Software is included only in the embedded (Work Log) copy. There the page has
        // already proved the developer is allocated to this project, and the server proves
        // it again on every write; in the Projects module the tab is Admin/PM as before.
        this.canManage = !!(user?.isAdmin || user?.isPM || (this.embedded && user?.isSoftware));
      });

    if (this.projectId) {
      this.load();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && this.projectId) {
      this.load();
    }
  }

  load(): void {
    if (!this.projectId) {
      return;
    }

    this.loading = true;

    forkJoin({
      tasks: this.taskService
        .getByProjectId(this.projectId)
        .pipe(catchError(() => of([] as ProjectTask[]))),
      employees: this.employeeService
        .getAllEmployees()
        .pipe(catchError(() => of([] as Employee[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ tasks, employees }) => {
        this.tasks = tasks ?? [];
        // Inactive staff are left out of the picker so new work cannot be assigned to
        // someone who has left; an existing task keeps its stored owner name either way.
        this.employees = (employees ?? []).filter((e) => e.isActive);
        this.loading = false;
      });
  }

  // ---------- derived ----------

  get filteredTasks(): ProjectTask[] {
    const search = this.searchText.trim().toLowerCase();

    return this.tasks.filter((task) => {
      if (search) {
        const matches =
          task.name.toLowerCase().includes(search) ||
          (task.taskNumber ?? '').toLowerCase().includes(search) ||
          (task.description ?? '').toLowerCase().includes(search) ||
          (task.ownerName ?? '').toLowerCase().includes(search);
        if (!matches) {
          return false;
        }
      }

      if (this.selectedSprints.length && !this.selectedSprints.includes(this.sprintKey(task))) {
        return false;
      }

      if (this.selectedOwners.length && !this.selectedOwners.includes(task.ownerName ?? '')) {
        return false;
      }

      if (this.selectedProgress.length && !this.selectedProgress.includes(task.progress)) {
        return false;
      }

      return true;
    });
  }

  /**
   * Tasks grouped by sprint, in the order the API returned them (sprint, then display order).
   * Unsprinted work collects under one heading at the end rather than being scattered.
   */
  get sprintGroups(): SprintGroup[] {
    const groups = new Map<string, ProjectTask[]>();

    for (const task of this.filteredTasks) {
      const key = this.sprintKey(task);
      const list = groups.get(key);
      if (list) {
        list.push(task);
      } else {
        groups.set(key, [task]);
      }
    }

    return [...groups.entries()].map(([sprint, tasks]) => ({
      sprint,
      tasks,
      doneCount: tasks.filter((t) => t.progress === 'Done').length,
    }));
  }

  private sprintKey(task: ProjectTask): string {
    return task.sprintNumber?.trim() || 'No sprint';
  }

  get sprintOptions(): { label: string; value: string }[] {
    const names = new Set<string>();
    for (const task of this.tasks) {
      names.add(this.sprintKey(task));
    }
    return [...names].sort().map((name) => ({ label: name, value: name }));
  }

  get ownerOptions(): { label: string; value: string }[] {
    const names = new Set<string>();
    for (const task of this.tasks) {
      if (task.ownerName) {
        names.add(task.ownerName);
      }
    }
    return [...names].sort().map((name) => ({ label: name, value: name }));
  }

  get employeeOptions(): { label: string; value: string }[] {
    return this.employees
      .map((e) => ({ label: e.name, value: e.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  get hasActiveFilters(): boolean {
    return (
      !!this.searchText.trim() ||
      this.selectedSprints.length > 0 ||
      this.selectedOwners.length > 0 ||
      this.selectedProgress.length > 0
    );
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedSprints = [];
    this.selectedOwners = [];
    this.selectedProgress = [];
  }

  get doneCount(): number {
    return this.tasks.filter((t) => t.progress === 'Done').length;
  }

  get blockedCount(): number {
    return this.tasks.filter((t) => t.progress === 'Blocked').length;
  }

  get overdueCount(): number {
    return this.tasks.filter((t) => this.isOverdue(t)).length;
  }

  /** Share of the backlog finished, for the header bar. */
  get donePercent(): number {
    if (!this.tasks.length) {
      return 0;
    }
    return Math.round((this.doneCount / this.tasks.length) * 100);
  }

  /** Shows only the states that are actually present, so the chips are never dead ends. */
  get quickFilters(): { label: string; value: string; count: number; severity: string }[] {
    return this.progressOptions
      .map((option) => ({
        label: option.label,
        value: option.value,
        count: this.tasks.filter((t) => t.progress === option.value).length,
        severity: this.progressSeverity(option.value) as string,
      }))
      .filter((chip) => chip.count > 0);
  }

  toggleProgressFilter(value: string): void {
    this.selectedProgress = this.selectedProgress.includes(value)
      ? this.selectedProgress.filter((v) => v !== value)
      : [...this.selectedProgress, value];
  }

  isProgressFilterActive(value: string): boolean {
    return this.selectedProgress.includes(value);
  }

  /**
   * Work that should have started but has not. Only flags tasks still at To Do: once
   * something is in progress the start date has served its purpose, and a Done task is
   * never late.
   */
  isOverdue(task: ProjectTask): boolean {
    if (!task.startDate || task.progress !== 'To Do') {
      return false;
    }
    const start = new Date(task.startDate);
    if (Number.isNaN(start.getTime())) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    return start.getTime() < today.getTime();
  }

  /**
   * Past its target date and not finished. Distinct from isOverdue(), which means "should
   * have started by now" — a task can be well underway and still late to deliver.
   */
  isPastTarget(task: ProjectTask): boolean {
    if (!task.targetDate || task.progress === 'Done') {
      return false;
    }
    const target = new Date(task.targetDate);
    if (Number.isNaN(target.getTime())) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    return target.getTime() < today.getTime();
  }

  // ---------- dialog ----------

  showAddDialog(): void {
    if (!this.projectId) {
      return;
    }
    this.isEditMode = false;
    this.editingId = null;
    this.editingTask = null;
    this.form = this.emptyForm();
    this.startDate = null;
    this.targetDate = null;
    this.completedDate = null;
    this.effortHours = null;
    this.effortNotes = null;
    this.existingEffortDisplay = '0h 0min';
    this.nextTaskNumber = this.computeNextTaskNumber();
    this.fieldTouched = {};
    // Cleared on every open. A request that never completed — dropped connection, the tab
    // backgrounded mid-save — left this true forever, and Save then returned silently on
    // every later attempt with no way for the user to tell why.
    this.saveInFlight = false;
    this.displayDialog = true;
  }

  /**
   * Keeps the two dates in order. The Target picker's minDate stops a date before the start
   * being picked; this covers moving the START forward past a target already set.
   */
  onStartDateChange(): void {
    if (this.startDate && this.targetDate && this.targetDate < this.startDate) {
      this.targetDate = null;
    }
  }

  /**
   * Works out the next task number from what the project already uses, mirroring the server
   * so the preview matches what actually gets saved: highest trailing number plus one,
   * keeping the existing prefix and zero-padding. Blank when nothing numeric is in use.
   */
  private computeNextTaskNumber(): string {
    const numbers = this.tasks
      .map(t => (t.taskNumber ?? '').trim())
      .filter(Boolean);

    if (!numbers.length) {
      return 'T-001';
    }

    const pattern = /^(.*?)(\d+)\s*$/;
    let highest = 0;
    let prefix = 'T-';
    let padding = 3;
    let sawNumeric = false;

    for (const number of numbers) {
      const match = pattern.exec(number);
      if (!match) {
        continue;
      }
      sawNumeric = true;
      const value = parseInt(match[2], 10);
      if (value >= highest) {
        highest = value;
        prefix = match[1];
        padding = match[2].length;
      }
    }

    if (!sawNumeric) {
      return '';
    }

    return `${prefix}${String(highest + 1).padStart(padding, '0')}`;
  }

  showEditDialog(task: ProjectTask): void {
    this.isEditMode = true;
    this.editingId = task.id;
    this.editingTask = task;
    this.form = {
      projectId: this.projectId,
      sprintNumber: task.sprintNumber ?? null,
      taskNumber: task.taskNumber ?? null,
      name: task.name,
      description: task.description ?? null,
      startDate: task.startDate ?? null,
      targetDate: task.targetDate ?? null,
      ownerId: task.ownerId ?? null,
      progress: task.progress,
      // Snapped to the nearest quarter the dropdown offers. Older rows (and anything the
      // API accepted before this form used fixed steps) can hold any value, which would
      // render the dropdown blank and silently reset to 0 on the next save.
      progressPercentage: this.snapPercent(task.progressPercentage ?? 0, task.progress),
      completedDate: task.completedDate ?? null,
      displayOrder: task.displayOrder ?? 0,
    };
    this.startDate = task.startDate ? new Date(task.startDate) : null;
    this.targetDate = task.targetDate ? new Date(task.targetDate) : null;
    this.completedDate = task.completedDate ? new Date(task.completedDate) : null;
    // Deliberately blank, not the current total: this field adds hours.
    this.effortHours = null;
    this.effortNotes = null;
    this.existingEffortDisplay = task.totalEffortDisplay || '0h 0min';
    // See showAddDialog: a stranded in-flight flag would make Save silently inert.
    this.saveInFlight = false;
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  /**
   * Moving to Done fills the completion date if it is blank, and moving away from Done
   * clears it — the server enforces the same rule, this just keeps the form honest while
   * it is open.
   */
  onProgressChange(): void {
    if (this.form.progress === 'Done') {
      this.completedDate ??= new Date();
      this.form.progressPercentage = 100;
    } else {
      this.completedDate = null;
      // 100% is not offered off Done, so a task reopened from Done would otherwise hold a
      // value with no matching option — the dropdown would render blank.
      if (this.form.progressPercentage === 100) {
        this.form.progressPercentage = 75;
      }
    }
  }

  save(): void {
    if (this.saveInFlight) {
      return;
    }

    // Every refusal below says why. These used to return silently, which made a blocked
    // save indistinguishable from a dead button — the one failure mode that gives the user
    // nothing to act on.
    this.fieldTouched['name'] = true;
    if (!this.form.name?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Task name required',
        detail: 'Give the task a name before saving.',
      });
      return;
    }

    // A value like 2.75 means the person intended hours they would not get, so the form
    // says so rather than saving without them.
    if (this.effortMinutesInvalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Check the effort hours',
        detail: 'Minutes must be 00–59 — enter 2.45 for 2h 45m, not 2.75.',
      });
      return;
    }

    if (!this.projectId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Could not save',
        detail: 'No project is selected for this task.',
      });
      return;
    }

    // Date-only, so a timezone offset cannot shift a date by a day.
    this.form.startDate = this.startDate ? this.toIsoDate(this.startDate) : null;
    this.form.targetDate = this.targetDate ? this.toIsoDate(this.targetDate) : null;
    this.form.completedDate = this.completedDate ? this.toIsoDate(this.completedDate) : null;
    this.form.projectId = this.projectId;

    // Only sent when there is something to log; the server ignores 0 either way.
    this.form.effortHours = this.effortHours && this.effortHours > 0 ? this.effortHours : null;
    this.form.effortNotes = this.form.effortHours ? this.effortNotes : null;

    this.saveInFlight = true;

    const request = this.editingId
      ? this.taskService.update(this.editingId, this.form)
      : this.taskService.add(this.form);

    const wasEdit = !!this.editingId;

    request
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.saveInFlight = false;
          this.messageService.add({
            severity: 'success',
            summary: wasEdit ? 'Updated' : 'Added',
            detail: `"${result.name}" has been saved.`,
          });
          this.displayDialog = false;
          this.load();
        },
        error: (err: HttpErrorResponse) => {
          // The dialog stays open so the typed values are not lost on a failed save.
          this.saveInFlight = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Could not save',
            detail: this.describeError(err, 'The task was not saved.'),
          });
        },
      });
  }

  // ---------- effort ----------

  /**
   * Swaps the edit dialog for the effort log on the same task.
   *
   * Closes the edit dialog rather than stacking a second one over it: the two share the
   * hours and notes fields, and leaving both open would make it ambiguous which one a save
   * applies to. Because that discards whatever is typed, it confirms first when anything
   * has been.
   */
  viewEffortHistory(): void {
    if (!this.editingTask) {
      return;
    }

    const task = this.editingTask;
    const open = () => {
      this.displayDialog = false;
      this.openEffortDialog(task);
    };

    if (!this.hasUnsavedTaskEdits()) {
      open();
      return;
    }

    this.confirmationService.confirm({
      message: 'Viewing the effort entries will close this form and discard your unsaved changes. Continue?',
      header: 'Discard changes?',
      icon: 'pi pi-exclamation-triangle',
      accept: open,
    });
  }

  /** True when the edit form differs from the task it was opened from. */
  private hasUnsavedTaskEdits(): boolean {
    const task = this.editingTask;
    if (!task) {
      return false;
    }

    return this.form.name !== task.name
      || (this.form.sprintNumber ?? null) !== (task.sprintNumber ?? null)
      || (this.form.taskNumber ?? null) !== (task.taskNumber ?? null)
      || (this.form.description ?? null) !== (task.description ?? null)
      || this.form.progress !== task.progress
      || this.form.progressPercentage !== (task.progressPercentage ?? 0)
      || (this.form.ownerId ?? null) !== (task.ownerId ?? null)
      || !!this.effortHours
      || !!this.effortNotes;
  }

  openEffortDialog(task: ProjectTask): void {
    this.effortTask = task;
    this.effortHours = null;
    this.effortNotes = null;
    this.effortDate = null;   // null means today, resolved server-side
    this.displayEffortDialog = true;
  }

  /**
   * The entries behind a task's total, for the column tooltip. A single number cannot show
   * who logged what, which is the first question anyone asks about a total.
   */
  effortBreakdown(task: ProjectTask): string {
    const entries = task.effortEntries ?? [];
    if (!entries.length) {
      return 'No effort logged yet';
    }
    return entries
      .map(e => `${e.hoursDisplay || '0h 0min'} · ${this.formatDate(e.effortDate)}`
        + (e.employeeName ? ` · ${e.employeeName}` : ''))
      .join('\n');
  }

  saveEffort(): void {
    if (this.effortInFlight || !this.effortTask || !this.effortHours || this.effortHours <= 0
      || this.effortMinutesInvalid) {
      return;
    }

    this.effortInFlight = true;

    this.taskService
      .addEffort({
        projectTaskId: this.effortTask.id,
        hours: this.effortHours,
        effortDate: this.effortDate ? this.toIsoDate(this.effortDate) : null,
        notes: this.effortNotes,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.effortInFlight = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Effort logged',
            detail: `Now ${updated.totalEffortDisplay} total.`,
          });
          this.displayEffortDialog = false;
          this.load();
        },
        error: (err: HttpErrorResponse) => {
          this.effortInFlight = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Could not log effort',
            detail: this.describeError(err, 'The hours were not saved.'),
          });
        },
      });
  }

  confirmDeleteEffort(entry: ProjectTaskEffort): void {
    this.confirmationService.confirm({
      message: `Remove this ${entry.hoursDisplay} entry? The task total will go down by that much.`,
      header: 'Remove effort entry',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteEffort(entry),
    });
  }

  private deleteEffort(entry: ProjectTaskEffort): void {
    this.taskService
      .deleteEffort(entry.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Entry removed',
            detail: `Now ${updated.totalEffortDisplay} total.`,
          });
          // Keep the open dialog in step with what was just removed.
          this.effortTask = updated;
          this.load();
        },
        error: (err: HttpErrorResponse) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Could not remove entry',
            detail: this.describeError(err, 'The entry was not removed.'),
          });
        },
      });
  }

  confirmDelete(task: ProjectTask): void {
    this.confirmationService.confirm({
      // Wording and header match the sibling plan tabs, so a delete prompt reads the same
      // wherever it appears on this page.
      message: `Are you sure you want to delete "${task.name}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteTask(task),
    });
  }

  private deleteTask(task: ProjectTask): void {
    // Errors are handled in the subscribe's error arm rather than with catchError(of(null)):
    // that pattern still emits, so the success toast fired on a failed delete and the row
    // reappeared on reload — indistinguishable from the button doing nothing.
    this.taskService
      .delete(task.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: `"${task.name}" has been removed.`,
          });
          this.load();
        },
        error: (err: HttpErrorResponse) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Could not delete',
            detail: this.describeError(err, 'The task was not deleted.'),
          });
        },
      });
  }

  /**
   * Turns a failed request into something actionable. A bare "please try again" hides the
   * difference between a permission problem, a stale row and a server fault, which is
   * exactly what makes a broken button hard to diagnose.
   */
  private describeError(err: HttpErrorResponse, fallback: string): string {
    switch (err.status) {
      case 0:
        return 'Could not reach the server. Check that the API is running.';
      case 401:
        return 'Your session has expired. Please sign in again.';
      case 403:
        return 'You do not have permission to change tasks on this project.';
      case 404:
        return 'This task no longer exists. It may have been removed already.';
      default:
        return `${fallback} (server returned ${err.status})`;
    }
  }

  // ---------- display ----------

  progressSeverity(progress: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (progress) {
      case 'Done':
        return 'success';
      case 'In Progress':
        return 'info';
      case 'Blocked':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  formatDate(iso?: string | null): string {
    if (!iso) {
      return '—';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  private emptyForm(): CreateProjectTaskDto {
    return {
      projectId: this.projectId,
      sprintNumber: null,
      taskNumber: null,
      name: '',
      description: null,
      startDate: null,
      targetDate: null,
      ownerId: null,
      progress: 'To Do',
      progressPercentage: 0,
      completedDate: null,
      displayOrder: 0,
    };
  }

  /** Date-only ISO, so a timezone offset cannot shift a date by a day. */
  private toIsoDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
